import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  DollarSign,
  PieChart,
  BarChart3,
  ExternalLink,
  Calendar,
  ArrowUpRight,
} from 'lucide-react';
import { financialYears, getFinancialData } from '../data/financialData';

const CHART_COLORS = {
  gold: 'rgba(184, 148, 31, 1)',
  goldFill: 'rgba(184, 148, 31, 0.85)',
  navy: 'rgba(11, 31, 58, 1)',
  navyFill: 'rgba(11, 31, 58, 0.85)',
  slate: 'rgba(100, 116, 139, 1)',
  slateFill: 'rgba(100, 116, 139, 0.85)',
  green: 'rgba(22, 101, 52, 1)',
  greenFill: 'rgba(22, 101, 52, 0.12)',
};

const PL_LINE_COLORS = {
  revenue: {
    border: '#B8941F',
    fill: 'rgba(184, 148, 31, 0.12)',
    point: '#B8941F',
  },
  expenses: {
    border: '#DC2626',
    fill: 'rgba(220, 38, 38, 0.10)',
    point: '#DC2626',
  },
  netProfit: {
    border: '#059669',
    fill: 'rgba(5, 150, 105, 0.12)',
    point: '#059669',
  },
};

// Distinct bar colors — all solid enough to read on light and dark backgrounds
const REVENUE_BAR_BACKGROUNDS = [
  'rgba(184, 148, 31, 0.92)',
  'rgba(11, 31, 58, 0.88)',
  'rgba(212, 175, 55, 0.88)',
  'rgba(71, 85, 105, 0.88)',
  'rgba(160, 124, 24, 0.92)',
  'rgba(26, 54, 93, 0.88)',
  'rgba(201, 162, 39, 0.90)',
  'rgba(100, 116, 139, 0.88)',
  'rgba(139, 105, 20, 0.92)',
  'rgba(45, 74, 111, 0.88)',
];

const REVENUE_BAR_BORDERS = [
  '#B8941F',
  '#0B1F3A',
  '#D4AF37',
  '#475569',
  '#92710F',
  '#1A365D',
  '#C9A227',
  '#64748B',
  '#8B6914',
  '#2D4A6F',
];

const REVENUE_BAR_BACKGROUNDS_DARK = [
  'rgba(212, 175, 55, 0.95)',
  'rgba(148, 163, 184, 0.90)',
  'rgba(184, 148, 31, 0.92)',
  'rgba(100, 116, 139, 0.92)',
  'rgba(201, 162, 39, 0.95)',
  'rgba(71, 85, 105, 0.92)',
  'rgba(212, 175, 55, 0.85)',
  'rgba(148, 163, 184, 0.85)',
  'rgba(184, 148, 31, 0.88)',
  'rgba(100, 116, 139, 0.90)',
];

const REVENUE_BAR_BORDERS_DARK = [
  '#D4AF37',
  '#CBD5E1',
  '#B8941F',
  '#94A3B8',
  '#C9A227',
  '#64748B',
  '#D4AF37',
  '#CBD5E1',
  '#B8941F',
  '#94A3B8',
];

const iconMap = {
  'Revenue Growth': TrendingUp,
  'Total Revenue': DollarSign,
  'EBITDA Margin': PieChart,
  'Net Profit': BarChart3,
};

const Financials = () => {
  const [selectedYear, setSelectedYear] = useState(2025);
  const [chartReady, setChartReady] = useState(false);
  const revenueChartRef = useRef(null);
  const plChartRef = useRef(null);
  const expenseChartRef = useRef(null);
  const chartInstances = useRef({ revenue: null, pl: null, expense: null });

  const data = getFinancialData(selectedYear);
  const { summary } = data;

  const getThemeColors = useCallback(() => {
    const style = getComputedStyle(document.documentElement);
    const fg = style.getPropertyValue('--foreground').trim();
    return {
      foreground: fg ? `hsl(${fg})` : '#0B1F3A',
      muted: style.getPropertyValue('--muted-foreground').trim()
        ? `hsl(${style.getPropertyValue('--muted-foreground').trim()})`
        : '#64748b',
      grid: 'rgba(128, 128, 128, 0.12)',
    };
  }, []);

  const destroyCharts = useCallback(() => {
    Object.values(chartInstances.current).forEach((chart) => chart?.destroy());
    chartInstances.current = { revenue: null, pl: null, expense: null };
  }, []);

  const buildCharts = useCallback(() => {
    if (!window.Chart || !revenueChartRef.current || !plChartRef.current || !expenseChartRef.current) {
      return;
    }

    destroyCharts();
    const colors = getThemeColors();
    const isDark = document.documentElement.classList.contains('dark');
    const barBackgrounds = isDark ? REVENUE_BAR_BACKGROUNDS_DARK : REVENUE_BAR_BACKGROUNDS;
    const barBorders = isDark ? REVENUE_BAR_BORDERS_DARK : REVENUE_BAR_BORDERS;

    chartInstances.current.revenue = new window.Chart(revenueChartRef.current.getContext('2d'), {
      type: 'bar',
      data: {
        labels: data.revenueByIndustry.labels,
        datasets: [{
          label: `Revenue FY ${selectedYear} (M USD)`,
          data: data.revenueByIndustry.values,
          backgroundColor: data.revenueByIndustry.values.map((_, i) =>
            barBackgrounds[i % barBackgrounds.length]
          ),
          borderColor: data.revenueByIndustry.values.map((_, i) =>
            barBorders[i % barBorders.length]
          ),
          borderWidth: 2,
          borderRadius: 3,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#0B1F3A',
            titleColor: '#D4AF37',
            bodyColor: '#fff',
            padding: 12,
            callbacks: {
              label: (ctx) => ` $${ctx.parsed.y}M`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: colors.grid },
            ticks: { color: colors.foreground, callback: (v) => `$${v}M` },
          },
          x: {
            grid: { display: false },
            ticks: { color: colors.foreground, font: { size: 10 }, maxRotation: 45 },
          },
        },
      },
    });

    chartInstances.current.pl = new window.Chart(plChartRef.current.getContext('2d'), {
      type: 'line',
      data: {
        labels: data.plTrend.labels,
        datasets: [
          {
            label: 'Revenue',
            data: data.plTrend.revenue,
            borderColor: PL_LINE_COLORS.revenue.border,
            backgroundColor: PL_LINE_COLORS.revenue.fill,
            borderWidth: 3,
            tension: 0.35,
            fill: true,
            pointRadius: 6,
            pointHoverRadius: 8,
            pointBackgroundColor: PL_LINE_COLORS.revenue.point,
            pointBorderColor: '#FFFFFF',
            pointBorderWidth: 2,
          },
          {
            label: 'Expenses',
            data: data.plTrend.expenses,
            borderColor: PL_LINE_COLORS.expenses.border,
            backgroundColor: PL_LINE_COLORS.expenses.fill,
            borderWidth: 3,
            tension: 0.35,
            fill: true,
            pointRadius: 6,
            pointHoverRadius: 8,
            pointBackgroundColor: PL_LINE_COLORS.expenses.point,
            pointBorderColor: '#FFFFFF',
            pointBorderWidth: 2,
          },
          {
            label: 'Net Profit',
            data: data.plTrend.netProfit,
            borderColor: PL_LINE_COLORS.netProfit.border,
            backgroundColor: PL_LINE_COLORS.netProfit.fill,
            borderWidth: 3,
            tension: 0.35,
            fill: true,
            pointRadius: 6,
            pointHoverRadius: 8,
            pointBackgroundColor: PL_LINE_COLORS.netProfit.point,
            pointBorderColor: '#FFFFFF',
            pointBorderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            labels: {
              color: colors.foreground,
              usePointStyle: true,
              padding: 20,
              font: { size: 13, weight: '600' },
              generateLabels: (chart) => {
                const defaults = window.Chart.defaults.plugins.legend.labels.generateLabels(chart);
                return defaults.map((label, i) => {
                  const borderColors = [
                    PL_LINE_COLORS.revenue.border,
                    PL_LINE_COLORS.expenses.border,
                    PL_LINE_COLORS.netProfit.border,
                  ];
                  return { ...label, fillStyle: borderColors[i], strokeStyle: borderColors[i], lineWidth: 3 };
                });
              },
            },
          },
          tooltip: {
            backgroundColor: '#0B1F3A',
            titleColor: '#D4AF37',
            bodyColor: '#fff',
            padding: 12,
            callbacks: {
              label: (ctx) => ` ${ctx.dataset.label}: $${ctx.parsed.y}M`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: colors.grid },
            ticks: { color: colors.foreground, callback: (v) => `$${v}M` },
          },
          x: {
            grid: { display: false },
            ticks: { color: colors.foreground },
          },
        },
      },
    });

    const expenseLabels = Object.keys(data.expenseBreakdown);
    const expenseValues = Object.values(data.expenseBreakdown);

    chartInstances.current.expense = new window.Chart(expenseChartRef.current.getContext('2d'), {
      type: 'doughnut',
      data: {
        labels: expenseLabels,
        datasets: [{
          data: expenseValues,
          backgroundColor: [
            'rgba(184, 148, 31, 0.9)',
            'rgba(11, 31, 58, 0.85)',
            'rgba(100, 116, 139, 0.8)',
            'rgba(184, 148, 31, 0.55)',
            'rgba(11, 31, 58, 0.55)',
            'rgba(100, 116, 139, 0.55)',
          ],
          borderWidth: 2,
          borderColor: getComputedStyle(document.body).backgroundColor,
          hoverOffset: 8,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: {
          legend: {
            position: 'right',
            labels: { color: colors.foreground, padding: 14, usePointStyle: true },
          },
          tooltip: {
            backgroundColor: '#0B1F3A',
            titleColor: '#D4AF37',
            bodyColor: '#fff',
            callbacks: {
              label: (ctx) => ` $${ctx.parsed}M (${((ctx.parsed / summary.totalExpenses) * 100).toFixed(1)}%)`,
            },
          },
        },
      },
    });
  }, [data, selectedYear, destroyCharts, getThemeColors, summary.totalExpenses]);

  useEffect(() => {
    if (window.Chart) {
      setChartReady(true);
      return undefined;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/chart.js';
    script.async = true;
    script.onload = () => setChartReady(true);
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    if (chartReady) {
      buildCharts();
    }
    return destroyCharts;
  }, [chartReady, buildCharts, destroyCharts, selectedYear]);

  return (
    <div className="min-h-screen pt-28 bg-background">
      {/* Hero */}
      <section className="relative border-b border-border bg-[#0B1F3A] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,#D4AF37,transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
                Investor Relations
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold mb-4">
                Financial Overview
              </h1>
              <p className="text-lg text-white/75 leading-relaxed">
                Interactive reporting across fiscal years. Explore revenue, profitability, and portfolio performance.
              </p>
            </div>

            {/* Year Switcher */}
            <div className="flex flex-col gap-3">
              <span className="text-xs uppercase tracking-wider text-white/50 flex items-center gap-2">
                <Calendar size={14} /> Select reporting year
              </span>
              <div className="inline-flex p-1 bg-white/10 border border-white/20 backdrop-blur-sm">
                {financialYears.map((year) => (
                  <button
                    key={year}
                    onClick={() => setSelectedYear(year)}
                    className={`px-8 py-3 text-sm font-semibold uppercase tracking-wider transition-all duration-300 ${
                      selectedYear === year
                        ? 'bg-[#D4AF37] text-[#0B1F3A] shadow-lg'
                        : 'text-white/70 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    FY {year}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Summary strip */}
      <section className="bg-muted/40 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedYear}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {[
                { label: 'Total Revenue', value: `$${summary.totalRevenue}M` },
                { label: 'Net Profit', value: `$${summary.netProfit}M` },
                { label: 'Profit Margin', value: `${summary.profitMargin}%` },
                { label: 'YoY Growth', value: `+${summary.revenueGrowth}%` },
              ].map((item) => (
                <div key={item.label} className="text-center lg:text-left">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{item.label}</div>
                  <div className="text-2xl lg:text-3xl font-semibold text-[#0B1F3A] dark:text-[#D4AF37]">
                    {item.value}
                  </div>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Highlights */}
      <section className="corporate-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="corporate-eyebrow mb-2">{data.label}</p>
            <h2 className="text-3xl md:text-4xl font-semibold">Financial Highlights</h2>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={selectedYear}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {data.highlights.map((item, index) => {
                const Icon = iconMap[item.label] || TrendingUp;
                return (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.06 }}
                    className="corporate-card p-8 group hover:border-[#B8941F]/50 hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-6">
                      <div className="w-12 h-12 bg-[#0B1F3A] dark:bg-[#D4AF37]/20 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-[#D4AF37]" />
                      </div>
                      <ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="text-3xl font-semibold text-[#0B1F3A] dark:text-[#D4AF37] mb-2">{item.value}</div>
                    <div className="text-base font-semibold mb-1">{item.label}</div>
                    <div className="text-sm text-muted-foreground">{item.description}</div>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Revenue chart + breakdown */}
      <section className="corporate-section bg-muted/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <p className="corporate-eyebrow mb-2">Portfolio Revenue</p>
              <h2 className="text-3xl font-semibold">Revenue by Industry</h2>
              <p className="text-muted-foreground mt-2">All figures in millions USD · {data.label}</p>
            </div>
          </div>

          <div className="corporate-card p-6 lg:p-8 mb-10">
            <div className="h-[380px]">
              <canvas ref={revenueChartRef} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {data.revenueBreakdown.map((item, index) => (
              <motion.div
                key={`${selectedYear}-${item.sector}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04 }}
                className="corporate-card p-5 hover:border-[#B8941F]/40 transition-colors"
              >
                <h4 className="font-semibold text-sm mb-3 leading-snug">{item.sector}</h4>
                <div className="text-2xl font-semibold text-[#B8941F] mb-3">${item.revenue}M</div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Growth</span>
                  <span className="font-medium text-green-600 dark:text-green-400">{item.growth}</span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>Margin</span>
                  <span className="font-medium">{item.margin}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* P&L + Expense */}
      <section className="corporate-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-12">
            <motion.div
              key={`pl-${selectedYear}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="xl:col-span-2 corporate-card p-6 lg:p-8"
            >
              <p className="corporate-eyebrow mb-2">Performance Trend</p>
              <h3 className="text-2xl font-semibold mb-6">Profit & Loss</h3>
              <div className="h-[340px]">
                <canvas ref={plChartRef} />
              </div>
            </motion.div>

            <motion.div
              key={`exp-${selectedYear}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="corporate-card p-6 lg:p-8"
            >
              <p className="corporate-eyebrow mb-2">Cost Structure</p>
              <h3 className="text-2xl font-semibold mb-6">Expense Breakdown</h3>
              <div className="h-[280px]">
                <canvas ref={expenseChartRef} />
              </div>
            </motion.div>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                value: `$${summary.totalRevenue}M`,
                label: `Total Revenue ${selectedYear}`,
                sub: `+${summary.revenueGrowth}% from ${selectedYear - 1}`,
                accent: 'text-[#B8941F]',
              },
              {
                value: `$${summary.totalExpenses}M`,
                label: `Total Expenses ${selectedYear}`,
                sub: `${summary.expenseRatio}% of revenue`,
                accent: 'text-slate-500',
              },
              {
                value: `$${summary.netProfit}M`,
                label: `Net Profit ${selectedYear}`,
                sub: `${summary.profitMargin}% profit margin`,
                accent: 'text-[#0B1F3A] dark:text-green-400',
              },
            ].map((card) => (
              <div key={card.label} className="corporate-card p-8 text-center border-t-4 border-t-[#B8941F]">
                <div className={`text-3xl font-semibold mb-2 ${card.accent}`}>{card.value}</div>
                <div className="text-base font-semibold mb-1">{card.label}</div>
                <div className="text-sm text-muted-foreground">{card.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investments */}
      <section className="corporate-section bg-muted/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="corporate-eyebrow mb-2">{data.label}</p>
            <h2 className="text-3xl font-semibold">Investment Opportunities</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.investments.map((item, index) => (
              <motion.div
                key={`${selectedYear}-${item.title}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="corporate-card p-8 hover:shadow-lg hover:border-[#B8941F]/40 transition-all"
              >
                <h3 className="text-lg font-semibold text-[#B8941F] mb-2">{item.title}</h3>
                <div className="text-3xl font-semibold mb-4">{item.amount}</div>
                <p className="text-sm text-muted-foreground mb-5 leading-relaxed">{item.description}</p>
                <ul className="space-y-2">
                  {item.points.map((point) => (
                    <li key={point} className="text-sm text-muted-foreground flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-[#B8941F] rounded-full shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ESG + Dashboard CTA */}
      <section className="corporate-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold mb-2">ESG Commitment</h2>
            <p className="text-muted-foreground">Sustainable business practices for long-term value</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {[
              { icon: '🌍', title: 'Environmental', items: ['Carbon neutral by 2026', '100% renewable energy', 'Sustainable supply chain', 'Green building standards'] },
              { icon: '👥', title: 'Social', items: ['Community development', 'Diverse workforce', 'Local sourcing', 'Education programs'] },
              { icon: '⚖️', title: 'Governance', items: ['Transparent reporting', 'Independent oversight', 'Ethical practices', 'Stakeholder engagement'] },
            ].map((block) => (
              <div key={block.title} className="corporate-card p-8">
                <div className="text-3xl mb-4">{block.icon}</div>
                <h3 className="text-xl font-semibold mb-4">{block.title}</h3>
                <ul className="space-y-2">
                  {block.items.map((li) => (
                    <li key={li} className="text-sm text-muted-foreground">• {li}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="bg-[#0B1F3A] text-white p-10 lg:p-14 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Interactive Analytics</p>
            <h2 className="text-3xl font-semibold mb-4">Full Financial Dashboard</h2>
            <p className="text-white/75 mb-8 max-w-2xl mx-auto">
              Explore {data.label} data with interactive charts, exportable reports, and detailed breakdowns on our Streamlit dashboard.
            </p>
            <a
              href={`https://arholdings-dashboard.streamlit.app/?year=${selectedYear}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B1F3A] px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#c5a028] transition-colors"
            >
              Launch FY {selectedYear} Dashboard
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Financials;
